import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Printer, ArrowRight } from "lucide-react";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");

export default function PayResult() {
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const ref = useRef();

  useEffect(() => {
    let t;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/public/fees/order/${orderId}`);
        setOrder(data);
        if (!["COMPLETED", "FAILED"].includes(data.state) && tries < 20) t = setTimeout(() => setTries((n) => n + 1), 3000);
      } catch { setOrder(false); }
    };
    poll();
    return () => clearTimeout(t);
  }, [orderId, tries]);

  const print = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Receipt ${order.receipt.receipt_no}</title><style>body{font-family:sans-serif;padding:30px;color:#111}table{width:100%;border-collapse:collapse}td,th{border:1px solid #ccc;padding:8px;text-align:left}</style></head><body>${ref.current.innerHTML}</body></html>`);
    w.document.close();
    w.print();
  };

  if (order === false) return <div className="py-24 text-center text-slate-600">Order not found.</div>;
  const pending = !order || !["COMPLETED", "FAILED"].includes(order.state);
  const r = order?.receipt;

  return (
    <div className="pub-mesh min-h-[80vh]">
      <div className="mx-auto max-w-2xl px-5 py-16 text-center" data-testid="pay-result">
        {pending ? (
          <><Loader2 size={40} className="mx-auto animate-spin text-indigo-600" /><h1 className="mt-5 text-2xl font-bold font-head">Confirming your payment…</h1><p className="mt-2 text-sm text-slate-500">Order {orderId} · status {order?.state || "…"}</p></>
        ) : order.state === "COMPLETED" ? (
          <>
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 size={38} /></motion.div>
            <h1 className="mt-5 text-3xl font-bold font-head" data-testid="pay-success">Payment successful</h1>
            <p className="mt-2 text-slate-600">Receipt sent to the registered mobile on WhatsApp.</p>
            {r && (
              <div className="pub-card mx-auto mt-8 max-w-lg p-6 text-left" data-testid="pay-receipt">
                <div ref={ref}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{r.school_name}</h2>
                  <p style={{ margin: "2px 0 12px", fontSize: 12, color: "#64748B" }}>Fee Receipt · {r.receipt_no} · {new Date(r.created_at).toLocaleString("en-IN")}</p>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <tbody>
                      <tr><td style={td}>Student</td><td style={td}><b>{r.student_name}</b> ({r.class_name}-{r.section})</td></tr>
                      {r.heads.map((h) => <tr key={h.name}><td style={td}>{h.name}</td><td style={td}>{inr(h.amount)}</td></tr>)}
                      <tr><td style={td}>Mode</td><td style={td}>{r.gateway === "phonepe" ? "PhonePe (Online)" : "Online (Simulated)"}</td></tr>
                      <tr><td style={{ ...td, fontWeight: 700 }}>Total Paid</td><td style={{ ...td, fontWeight: 700, color: "#059669" }}>{inr(r.total)}</td></tr>
                    </tbody>
                  </table>
                  <p style={{ marginTop: 10, fontSize: 11, color: "#64748B" }}>Txn Ref: {order.merchant_order_id}</p>
                </div>
                <button className="pub-btn mt-5 w-full" onClick={print} data-testid="pay-print-btn"><Printer size={15} /> Print / Save Receipt</button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-rose-100 text-rose-600"><XCircle size={38} /></div>
            <h1 className="mt-5 text-3xl font-bold font-head" data-testid="pay-failed">Payment not completed</h1>
            <p className="mt-2 text-slate-600">No amount was charged. You can try again.</p>
          </>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {order && <Link to={`/s/${order.school_code}/fees`} className="pub-btn pub-btn-outline" data-testid="pay-again-link">{order.state === "FAILED" ? "Try again" : "Pay another amount"}</Link>}
          {order && <Link to={`/s/${order.school_code}`} className="pub-btn">School home <ArrowRight size={15} /></Link>}
        </div>
      </div>
    </div>
  );
}

const td = { border: "1px solid #E2E8F0", padding: "6px 8px" };
